import React, { useMemo } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// PipeSequence.jsx
//
// Single-row 3D-style sequence: one continuous isometric pipe receding into the
// scene (perspective scale along the chain). Dark slate canvas, copper barrel,
// neon green/blue joint rings, TDC arrows, labels like pipeId_pipeEnd.
//
// TDC: angle_deg = (tdc_mm / (π × OD)) × 360  (same as optifit / pipefitter)
//
// Props:
//   apiResponse  — { success, data: { recordList: [...] } }
//   pipesPerRow  — legacy; ignored — all segments render in one horizontal row
//   defaultOD    — fallback OD (mm) if not on record (default 508)
// ─────────────────────────────────────────────────────────────────────────────

const PI = Math.PI;

// Theme
const BG_DEEP = "#ffffff";
const BG_PANEL = "#f9fafb";
const COPPER = { hi: "#d4a574", mid: "#9d6b3a", base: "#6b4423", dk: "#3d2815" };
const NEON_G = "#39ff9a";
const NEON_G_DIM = "#8ef5c4";
const NEON_B = "#38c6ff";
const RING_ALT = [NEON_G, NEON_B];

function shade(hex, p) {
  const n = parseInt(hex.replace("#", ""), 16);
  const c = (v) => Math.min(255, Math.max(0, v));
  return (
    "#" +
    [
      c(((n >> 16) & 255) + p),
      c(((n >> 8) & 255) + p),
      c((n & 255) + p),
    ]
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("")
  );
}

function tdcToDeg(tdc_mm, od) {
  return (tdc_mm / (PI * od)) * 360;
}

const ISO_DEG = 30;
const ISO_AX = Math.cos((ISO_DEG * PI) / 180);
const ISO_AY = -Math.sin((ISO_DEG * PI) / 180);
const ISO_PX = -ISO_AY;
const ISO_PY = ISO_AX;

function svgIsoFlange(cx, cy, col, rotDeg, scale = 1) {
  const hi = shade(col, 55);
  const dk = shade(col, -42);
  const rx = 11 * scale;
  const ry = 26 * scale;
  const id = `fl${cx.toFixed(0)}_${cy.toFixed(0)}`;
  return `
    <defs>
      <linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stop-color="${hi}"/>
        <stop offset="35%"  stop-color="${col}"/>
        <stop offset="100%" stop-color="${dk}"/>
      </linearGradient>
    </defs>
    <g transform="rotate(${rotDeg.toFixed(2)} ${cx.toFixed(2)} ${cy.toFixed(2)})">
      <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="url(#${id})" stroke="${dk}" stroke-width="${(1.1 * scale).toFixed(2)}"/>
      <line x1="${cx - 10 * scale}" y1="${cy - ry + 6 * scale}" x2="${cx + 10 * scale}" y2="${cy - ry + 6 * scale}" stroke="${hi}" stroke-width="${(1.5 * scale).toFixed(2)}" opacity="0.45"/>
    </g>
  `;
}

/** Neon ring at joint (ellipse aligned to pipe axis) */
function svgJointRing(cx, cy, rotDeg, scale, ringColor) {
  const rx = 13 * scale;
  const ry = 30 * scale;
  const glow = ringColor === NEON_G ? "rgba(57,255,154,0.35)" : "rgba(56,198,255,0.35)";
  return `
    <g transform="rotate(${rotDeg.toFixed(2)} ${cx.toFixed(2)} ${cy.toFixed(2)})">
      <ellipse cx="${cx}" cy="${cy}" rx="${rx + 2}" ry="${ry + 2}" fill="none" stroke="${glow}" stroke-width="4" opacity="0.9"/>
      <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="none" stroke="${ringColor}" stroke-width="${(2.2 * scale).toFixed(2)}"/>
    </g>
  `;
}

function svgIsoPipeShell(Lc, PW, FL, PH, tdc, od, scale = 1) {
  const ax = ISO_AX;
  const ay = ISO_AY;
  const px = ISO_PX;
  const py = ISO_PY;

  const bodyStart = {
    x: Lc.x + ax * ((FL / 2) * scale),
    y: Lc.y + ay * ((FL / 2) * scale),
  };
  const bodyEnd = {
    x: Lc.x + ax * ((FL / 2 + PW) * scale),
    y: Lc.y + ay * ((FL / 2 + PW) * scale),
  };
  const R = (PH / 2) * scale;
  const angleDeg = tdcToDeg(tdc || 0, od);
  const angleRad = (angleDeg * PI) / 180;
  const seamOff = R * Math.cos(angleRad);

  const hi = shade(COPPER.hi, 0);
  const mid = shade(COPPER.mid, 0);
  const col = shade(COPPER.base, 0);
  const dk = shade(COPPER.dk, 0);
  const id = `iso${Lc.x.toFixed(0)}_${Lc.y.toFixed(0)}`;

  const tlx = bodyStart.x + px * R;
  const tly = bodyStart.y + py * R;
  const trx = bodyEnd.x + px * R;
  const try_ = bodyEnd.y + py * R;
  const blx = bodyStart.x - px * R;
  const bly = bodyStart.y - py * R;
  const brx = bodyEnd.x - px * R;
  const bry = bodyEnd.y - py * R;

  const g1x = (tlx + trx) / 2;
  const g1y = (tly + try_) / 2;
  const g2x = (blx + brx) / 2;
  const g2y = (bly + bry) / 2;
  const gradAngle = (Math.atan2(g2y - g1y, g2x - g1x) * 180) / PI;

  const seamSx = bodyStart.x + px * seamOff;
  const seamSy = bodyStart.y + py * seamOff;
  const seamEx = bodyEnd.x + px * seamOff;
  const seamEy = bodyEnd.y + py * seamOff;
  const seamCol = shade(dk, 25);

  return `
    <defs>
      <linearGradient id="${id}" gradientTransform="rotate(${gradAngle.toFixed(1)} ${((tlx + brx) / 2).toFixed(1)} ${((tly + bry) / 2).toFixed(1)})">
        <stop offset="0%"   stop-color="${hi}"/>
        <stop offset="22%"  stop-color="${mid}"/>
        <stop offset="72%"  stop-color="${col}"/>
        <stop offset="100%" stop-color="${dk}"/>
      </linearGradient>
    </defs>
    <polygon
      points="${tlx},${tly} ${trx},${try_} ${brx},${bry} ${blx},${bly}"
      fill="url(#${id})"
      stroke="${shade(dk, 15)}"
      stroke-width="${(0.85 * scale).toFixed(2)}"
      stroke-linejoin="round"
    />
    <line
      x1="${seamSx}" y1="${seamSy}" x2="${seamEx}" y2="${seamEy}"
      stroke="${seamCol}"
      stroke-width="${(2.1 * scale).toFixed(2)}"
      stroke-linecap="round"
      opacity="0.92"
    />
    <line
      x1="${seamSx}" y1="${seamSy}" x2="${seamEx}" y2="${seamEy}"
      stroke="${shade(hi, -35)}"
      stroke-width="${(0.9 * scale).toFixed(2)}"
      stroke-linecap="round"
      opacity="0.55"
    />
  `;
}

/** TDC arrow: outward from barrel, angle from seam position */
function svgTdcArrow(Lc, PW, FL, PH, tdc, od, scale) {
  const ax = ISO_AX;
  const ay = ISO_AY;
  const px = ISO_PX;
  const py = ISO_PY;
  const bodyStart = {
    x: Lc.x + ax * ((FL / 2) * scale),
    y: Lc.y + ay * ((FL / 2) * scale),
  };
  const bodyEnd = {
    x: Lc.x + ax * ((FL / 2 + PW) * scale),
    y: Lc.y + ay * ((FL / 2 + PW) * scale),
  };
  const midx = (bodyStart.x + bodyEnd.x) / 2;
  const midy = (bodyStart.y + bodyEnd.y) / 2;
  const theta = (tdcToDeg(tdc || 0, od) * PI) / 180;
  const ox = px * Math.cos(theta) - py * Math.sin(theta);
  const oy = px * Math.sin(theta) + py * Math.cos(theta);
  const L = 18 * scale;
  const tipx = midx + ox * L;
  const tipy = midy + oy * L;
  const hw = 5 * scale;
  const bx = tipx - ox * hw;
  const by = tipy - oy * hw;
  const perp = { x: -oy, y: ox };
  return `
    <line x1="${midx}" y1="${midy}" x2="${bx}" y2="${by}" stroke="${NEON_G}" stroke-width="${(2 * scale).toFixed(2)}" stroke-linecap="round" opacity="0.95"/>
    <polygon
      points="${tipx},${tipy} ${bx + perp.x * hw * 0.55},${by + perp.y * hw * 0.55} ${bx - perp.x * hw * 0.55},${by - perp.y * hw * 0.55}"
      fill="${NEON_G}"
      stroke="${shade(NEON_G, -40)}"
      stroke-width="0.5"
    />
  `;
}

function buildSingleRowSVG(rowPipes, defaultOD) {
  const BASE_PW = 108;
  const BASE_PH = 44;
  const BASE_FL = 16;
  const N = rowPipes.length;
  const PAD = 28;
  const rotDeg = (Math.atan2(ISO_AY, ISO_AX) * 180) / PI;

  const PIPE_Y = 56;
  const scale0 = Math.max(0.55, 1 - 0.34 * (0 / Math.max(N - 1, 1)));
  const FL0 = BASE_FL * scale0;
  let Lc = { x: PAD + FL0 / 2, y: PIPE_Y };

  const parts = [];
  const rawAnchors = [];
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  const includePoint = (x, y) => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  };

  rowPipes.forEach((pipe, i) => {
    const pers = 1 - 0.34 * (i / Math.max(N - 1, 1));
    const scale = Math.max(0.55, pers);
    const PW = BASE_PW * scale;
    const FL = BASE_FL * scale;
    const PH = BASE_PH * scale;

    const pipeOD = pipe.od || defaultOD;
    const Rc = {
      x: Lc.x + (PW + FL) * ISO_AX,
      y: Lc.y + (PW + FL) * ISO_AY,
    };

    const ringCol = RING_ALT[i % 2];
    rawAnchors.push(Lc.x);

    if (i === 0) {
      parts.push(svgJointRing(Lc.x, Lc.y, rotDeg, scale, RING_ALT[0]));
    }

    parts.push(svgIsoPipeShell(Lc, PW, FL, PH, pipe.tdc ?? 0, pipeOD, scale));
    parts.push(svgIsoFlange(Lc.x, Lc.y, COPPER.mid, rotDeg, scale));
    parts.push(svgIsoFlange(Rc.x, Rc.y, COPPER.mid, rotDeg, scale));
    parts.push(svgTdcArrow(Lc, PW, FL, PH, pipe.tdc ?? 0, pipeOD, scale));

    parts.push(svgJointRing(Rc.x, Rc.y, rotDeg, scale, RING_ALT[(i + 1) % 2]));

    const ax = ISO_AX;
    const ay = ISO_AY;
    const px = ISO_PX;
    const py = ISO_PY;
    const bodyStart = {
      x: Lc.x + ax * ((FL / 2) * scale),
      y: Lc.y + ay * ((FL / 2) * scale),
    };
    const bodyEnd = {
      x: Lc.x + ax * ((FL / 2 + PW) * scale),
      y: Lc.y + ay * ((FL / 2 + PW) * scale),
    };
    const midx = (bodyStart.x + bodyEnd.x) / 2;
    const midy = (bodyStart.y + bodyEnd.y) / 2;
    const off = (PH / 2 + 22) * scale;
    const lx = midx + px * off;
    const ly = midy + py * off;
    const labelMain = `${pipe.pipeId || `PIPE-${pipe.id}`}_${pipe.pipeEnd ?? ""}`;
    const fs = (11 * scale).toFixed(2);
    const fsTdc = (8 * scale).toFixed(2);
    const tdcStr = `${tdcToDeg(pipe.tdc ?? 0, pipeOD).toFixed(1)}°`;

    parts.push(`
      <text x="${lx}" y="${ly}" text-anchor="start" font-size="${fs}" font-weight="700" fill="${i % 2 === 0 ? NEON_G_DIM : NEON_G}" font-family="system-ui,Segoe UI,sans-serif" style="text-shadow: 0 0 8px rgba(57,255,154,0.35)">${labelMain}</text>
      <text x="${lx}" y="${ly + 12 * scale}" text-anchor="start" font-size="${fsTdc}" font-weight="600" fill="rgba(180,200,215,0.75)" font-family="system-ui,sans-serif">TDC ${tdcStr} · ${pipe.pipeStart ?? ""}→${pipe.pipeEnd ?? ""}</text>
    `);

    const bcx = (Lc.x + Rc.x) / 2;
    const badgeY = midy - PH / 2 - 14 * scale;
    parts.push(`
      <circle cx="${bcx}" cy="${badgeY}" r="${7 * scale}" fill="${BG_PANEL}" stroke="${ringCol}" stroke-width="1.2"/>
      <text x="${bcx}" y="${badgeY + 4 * scale}" text-anchor="middle" font-size="${(9 * scale).toFixed(2)}" font-weight="700" fill="${NEON_G_DIM}" font-family="system-ui,sans-serif">${pipe.SlNo + 1}</text>
    `);

    const radial = 30 * scale;
    includePoint(Lc.x - radial - 4, Lc.y - radial - 8);
    includePoint(Lc.x + radial + 4, Lc.y + radial + 8);
    includePoint(Rc.x - radial - 4, Rc.y - radial - 8);
    includePoint(Rc.x + radial + 4, Rc.y + radial + 8);
    includePoint(lx, ly - 14 * scale);
    includePoint(lx + 170, ly + 22 * scale);
    includePoint(bcx - 10 * scale, badgeY - 10 * scale);
    includePoint(bcx + 10 * scale, badgeY + 10 * scale);

    Lc = Rc;
  });

  if (!Number.isFinite(minX)) {
    minX = 0;
    minY = 0;
    maxX = 400;
    maxY = 200;
  }

  const tx = PAD - minX;
  const ty = PAD - minY;
  const SVG_W = Math.ceil(maxX - minX + PAD * 2);
  const SVG_H = Math.ceil(maxY - minY + PAD * 2);
  const anchors = rawAnchors.map((x) => Math.max(0, x + tx - PAD));

  return {
    svg: `<svg width="${SVG_W}" height="${SVG_H}" viewBox="0 0 ${SVG_W} ${SVG_H}" xmlns="http://www.w3.org/2000/svg" style="display:block"><g transform="translate(${tx} ${ty})">${parts.join("")}</g></svg>`,
    anchors,
  };
}

const Legend = () => (
  <div
    style={{
      display: "flex",
      gap: 16,
      flexWrap: "wrap",
      alignItems: "center",
      padding: "10px 14px",
      background: "#f8f8f8",
      border: "1px solid #f0f0f0",
      borderRadius: 8,
      marginBottom: 14,
    }}
  >
    <span style={{ fontSize: 11.5, color: "#777" }}>
      TDC&nbsp;&nbsp;
      <strong style={{ color: "#444" }}>+ve</strong> clockwise&nbsp;&nbsp;·&nbsp;&nbsp;
      <strong style={{ color: "#444" }}>−ve</strong> anticlockwise&nbsp;
      <span style={{ color: "#bbb" }}>(from PipeStart)</span>
    </span>
    <span style={{ fontSize: 11.5, color: "#999" }}>
      Seam line + arrow show circumferential offset; rings mark joints along one continuous run.
    </span>
  </div>
);

/**
 * @param {object}  apiResponse   { success, data: { recordList } }
 * @param {number}  pipesPerRow   Unused — all segments are drawn in a single row.
 * @param {number}  defaultOD     Fallback OD (mm). Default 508
 */
const PipeSequence = ({ apiResponse, pipesPerRow: _pipesPerRow = 10, defaultOD = 508 }) => {
  const allPipes = useMemo(() => {
    if (!apiResponse?.data?.recordList) return [];
    return [...apiResponse.data.recordList].sort((a, b) => a.SlNo - b.SlNo);
  }, [apiResponse]);

  const graphData = useMemo(
    () => (allPipes.length ? buildSingleRowSVG(allPipes, defaultOD) : { svg: "", anchors: [] }),
    [allPipes, defaultOD],
  );
  const svgMarkup = graphData.svg;

  if (!allPipes.length) {
    return (
      <div
        style={{
          padding: 24,
          color: "#aaa",
          fontSize: 14,
          fontFamily: "system-ui",
          background: BG_PANEL,
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        No pipe sequence data available.
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        padding: "18px 20px",
        background: BG_DEEP,
        borderRadius: 14,
        border: "1px solid #eeeff1",
      }}
    >
      <Legend />

      <div
        style={{
          background: BG_PANEL,
          borderRadius: 12,
          border: "1px solid #eeeff1",
          padding: "16px 14px 14px",
          overflowX: "auto",
          scrollbarWidth: "thin",
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: svgMarkup }} />
      </div>
    </div>
  );
};

export default PipeSequence;
